import type { ImageMetadata, ComponentCategory, ConditionState, RestorePhase } from '../types';

/**
 * Generate placeholder image URLs using Lorem Picsum
 * Reliable placeholder service with consistent car-themed images
 */
function placeholderUrl(id: string, size: 'thumb' | 'medium' | 'full'): string {
  // Create a consistent seed from the ID string for reproducible images
  const seed = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);

  const sizes = {
    thumb: { w: 400, h: 300 },
    medium: { w: 800, h: 600 },
    full: { w: 1600, h: 1200 },
  };
  const { w, h } = sizes[size];
  // Use Lorem Picsum with seed for consistent placeholder images
  return `https://picsum.photos/seed/${seed}/${w}/${h}`;
}

/**
 * Demo images for the 1970 Chevelle SS (v-001)
 */
const chevelleImages: ImageMetadata[] = [
  // Engine images
  {
    id: 'img-001-001',
    vehicleId: 'v-001',
    filename: 'chevelle_engine_as_received_001.jpg',
    url: placeholderUrl('chevelle-engine-1', 'full'),
    thumbnailUrl: placeholderUrl('chevelle-engine-1', 'thumb'),
    mediumUrl: placeholderUrl('chevelle-engine-1', 'medium'),
    component: 'engine',
    componentDetail: 'LS6 454 big block',
    condition: 'as-received',
    phase: 'initial-assessment',
    confidence: 94,
    tags: ['454', 'big block', 'LS6', 'numbers matching', 'original'],
    capturedAt: '2024-03-15T10:30:00Z',
    classifiedAt: '2024-03-15T10:31:00Z',
    afterImageId: 'img-001-002',
    isHeroImage: false,
  },
  {
    id: 'img-001-002',
    vehicleId: 'v-001',
    filename: 'chevelle_engine_restored_001.jpg',
    url: placeholderUrl('chevelle-engine-2', 'full'),
    thumbnailUrl: placeholderUrl('chevelle-engine-2', 'thumb'),
    mediumUrl: placeholderUrl('chevelle-engine-2', 'medium'),
    component: 'engine',
    componentDetail: 'LS6 454 big block',
    condition: 'restored',
    phase: 'assembly',
    confidence: 97,
    tags: ['454', 'big block', 'LS6', 'rebuilt', 'detailed'],
    capturedAt: '2024-09-20T14:00:00Z',
    classifiedAt: '2024-09-20T14:01:00Z',
    beforeImageId: 'img-001-001',
    isHeroImage: true,
  },
  {
    id: 'img-001-003',
    vehicleId: 'v-001',
    filename: 'chevelle_engine_carburetor.jpg',
    url: placeholderUrl('chevelle-carb-1', 'full'),
    thumbnailUrl: placeholderUrl('chevelle-carb-1', 'thumb'),
    mediumUrl: placeholderUrl('chevelle-carb-1', 'medium'),
    component: 'fuel',
    componentDetail: 'Holley 780 carburetor',
    condition: 'disassembly',
    phase: 'teardown',
    confidence: 91,
    tags: ['Holley', '780 CFM', 'carburetor', 'vacuum secondary'],
    capturedAt: '2024-04-02T09:15:00Z',
    classifiedAt: '2024-04-02T09:16:00Z',
    isHeroImage: false,
  },
  // Suspension
  {
    id: 'img-001-004',
    vehicleId: 'v-001',
    filename: 'chevelle_front_suspension_before.jpg',
    url: placeholderUrl('chevelle-susp-1', 'full'),
    thumbnailUrl: placeholderUrl('chevelle-susp-1', 'thumb'),
    mediumUrl: placeholderUrl('chevelle-susp-1', 'medium'),
    component: 'suspension',
    componentDetail: 'front A-arms and spindles',
    condition: 'as-received',
    phase: 'initial-assessment',
    confidence: 89,
    tags: ['A-arms', 'spindles', 'ball joints', 'worn', 'rust'],
    capturedAt: '2024-03-16T11:00:00Z',
    classifiedAt: '2024-03-16T11:01:00Z',
    afterImageId: 'img-001-005',
    isHeroImage: false,
  },
  {
    id: 'img-001-005',
    vehicleId: 'v-001',
    filename: 'chevelle_front_suspension_after.jpg',
    url: placeholderUrl('chevelle-susp-2', 'full'),
    thumbnailUrl: placeholderUrl('chevelle-susp-2', 'thumb'),
    mediumUrl: placeholderUrl('chevelle-susp-2', 'medium'),
    component: 'suspension',
    componentDetail: 'front A-arms and spindles',
    condition: 'installed',
    phase: 'assembly',
    confidence: 95,
    tags: ['A-arms', 'spindles', 'new ball joints', 'powder coated'],
    capturedAt: '2024-08-10T15:30:00Z',
    classifiedAt: '2024-08-10T15:31:00Z',
    beforeImageId: 'img-001-004',
    isHeroImage: false,
  },
  // Body
  {
    id: 'img-001-006',
    vehicleId: 'v-001',
    filename: 'chevelle_body_quarter_rust.jpg',
    url: placeholderUrl('chevelle-body-1', 'full'),
    thumbnailUrl: placeholderUrl('chevelle-body-1', 'thumb'),
    mediumUrl: placeholderUrl('chevelle-body-1', 'medium'),
    component: 'body',
    componentDetail: 'rear quarter panel',
    condition: 'as-received',
    phase: 'initial-assessment',
    confidence: 92,
    tags: ['quarter panel', 'rust', 'body work needed', 'driver side'],
    capturedAt: '2024-03-15T14:00:00Z',
    classifiedAt: '2024-03-15T14:01:00Z',
    afterImageId: 'img-001-007',
    isHeroImage: false,
  },
  {
    id: 'img-001-007',
    vehicleId: 'v-001',
    filename: 'chevelle_body_quarter_repaired.jpg',
    url: placeholderUrl('chevelle-body-2', 'full'),
    thumbnailUrl: placeholderUrl('chevelle-body-2', 'thumb'),
    mediumUrl: placeholderUrl('chevelle-body-2', 'medium'),
    component: 'body',
    componentDetail: 'rear quarter panel',
    condition: 'repaired',
    phase: 'restoration',
    confidence: 96,
    tags: ['quarter panel', 'metal work', 'welded', 'straight'],
    capturedAt: '2024-06-25T10:30:00Z',
    classifiedAt: '2024-06-25T10:31:00Z',
    beforeImageId: 'img-001-006',
    isHeroImage: false,
  },
  {
    id: 'img-001-008',
    vehicleId: 'v-001',
    filename: 'chevelle_body_painted.jpg',
    url: placeholderUrl('chevelle-body-3', 'full'),
    thumbnailUrl: placeholderUrl('chevelle-body-3', 'thumb'),
    mediumUrl: placeholderUrl('chevelle-body-3', 'medium'),
    component: 'body',
    componentDetail: 'full body exterior',
    condition: 'painted',
    phase: 'finishing',
    confidence: 98,
    tags: ['Cranberry Red', 'base/clear', 'wet sanded', 'buffed'],
    capturedAt: '2024-10-05T16:00:00Z',
    classifiedAt: '2024-10-05T16:01:00Z',
    isHeroImage: true,
  },
  // Interior
  {
    id: 'img-001-009',
    vehicleId: 'v-001',
    filename: 'chevelle_interior_dash_before.jpg',
    url: placeholderUrl('chevelle-int-1', 'full'),
    thumbnailUrl: placeholderUrl('chevelle-int-1', 'thumb'),
    mediumUrl: placeholderUrl('chevelle-int-1', 'medium'),
    component: 'interior',
    componentDetail: 'dashboard and gauges',
    condition: 'as-received',
    phase: 'initial-assessment',
    confidence: 88,
    tags: ['dashboard', 'gauges', 'cracked', 'SS gauges', 'original'],
    capturedAt: '2024-03-17T09:00:00Z',
    classifiedAt: '2024-03-17T09:01:00Z',
    afterImageId: 'img-001-010',
    isHeroImage: false,
  },
  {
    id: 'img-001-010',
    vehicleId: 'v-001',
    filename: 'chevelle_interior_dash_after.jpg',
    url: placeholderUrl('chevelle-int-2', 'full'),
    thumbnailUrl: placeholderUrl('chevelle-int-2', 'thumb'),
    mediumUrl: placeholderUrl('chevelle-int-2', 'medium'),
    component: 'interior',
    componentDetail: 'dashboard and gauges',
    condition: 'restored',
    phase: 'assembly',
    confidence: 94,
    tags: ['dashboard', 'gauges', 'restored', 'SS gauges', 'working'],
    capturedAt: '2024-11-01T11:30:00Z',
    classifiedAt: '2024-11-01T11:31:00Z',
    beforeImageId: 'img-001-009',
    isHeroImage: false,
  },
  // Wheels
  {
    id: 'img-001-011',
    vehicleId: 'v-001',
    filename: 'chevelle_wheels_ss.jpg',
    url: placeholderUrl('chevelle-wheel-1', 'full'),
    thumbnailUrl: placeholderUrl('chevelle-wheel-1', 'thumb'),
    mediumUrl: placeholderUrl('chevelle-wheel-1', 'medium'),
    component: 'wheels',
    componentDetail: 'SS 5-spoke wheels 15x7',
    condition: 'restored',
    phase: 'finishing',
    confidence: 96,
    tags: ['SS wheels', '15x7', 'Redline tires', 'restored', 'chrome'],
    capturedAt: '2024-10-20T14:00:00Z',
    classifiedAt: '2024-10-20T14:01:00Z',
    isHeroImage: false,
  },
  // Exhaust
  {
    id: 'img-001-012',
    vehicleId: 'v-001',
    filename: 'chevelle_exhaust_headers.jpg',
    url: placeholderUrl('chevelle-exh-1', 'full'),
    thumbnailUrl: placeholderUrl('chevelle-exh-1', 'thumb'),
    mediumUrl: placeholderUrl('chevelle-exh-1', 'medium'),
    component: 'exhaust',
    componentDetail: 'long tube headers',
    condition: 'installed',
    phase: 'assembly',
    confidence: 93,
    tags: ['headers', 'long tube', 'ceramic coated', 'big block'],
    capturedAt: '2024-09-25T13:00:00Z',
    classifiedAt: '2024-09-25T13:01:00Z',
    isHeroImage: false,
  },
];

/**
 * Demo images for the 1967 Mustang Fastback (v-002)
 */
const mustangImages: ImageMetadata[] = [
  {
    id: 'img-002-001',
    vehicleId: 'v-002',
    filename: 'mustang_engine_289_before.jpg',
    url: placeholderUrl('mustang-engine-1', 'full'),
    thumbnailUrl: placeholderUrl('mustang-engine-1', 'thumb'),
    mediumUrl: placeholderUrl('mustang-engine-1', 'medium'),
    component: 'engine',
    componentDetail: '289 V8 Hi-Po',
    condition: 'as-received',
    phase: 'initial-assessment',
    confidence: 92,
    tags: ['289', 'Hi-Po', 'K-code', 'solid lifter', 'original'],
    capturedAt: '2024-01-12T10:00:00Z',
    classifiedAt: '2024-01-12T10:01:00Z',
    afterImageId: 'img-002-002',
    isHeroImage: false,
  },
  {
    id: 'img-002-002',
    vehicleId: 'v-002',
    filename: 'mustang_engine_289_restored.jpg',
    url: placeholderUrl('mustang-engine-2', 'full'),
    thumbnailUrl: placeholderUrl('mustang-engine-2', 'thumb'),
    mediumUrl: placeholderUrl('mustang-engine-2', 'medium'),
    component: 'engine',
    componentDetail: '289 V8 Hi-Po',
    condition: 'restored',
    phase: 'assembly',
    confidence: 97,
    tags: ['289', 'Hi-Po', 'rebuilt', 'detailed', 'show quality'],
    capturedAt: '2024-08-15T15:00:00Z',
    classifiedAt: '2024-08-15T15:01:00Z',
    beforeImageId: 'img-002-001',
    isHeroImage: true,
  },
  {
    id: 'img-002-003',
    vehicleId: 'v-002',
    filename: 'mustang_body_fastback.jpg',
    url: placeholderUrl('mustang-body-1', 'full'),
    thumbnailUrl: placeholderUrl('mustang-body-1', 'thumb'),
    mediumUrl: placeholderUrl('mustang-body-1', 'medium'),
    component: 'body',
    componentDetail: 'fastback profile',
    condition: 'painted',
    phase: 'finishing',
    confidence: 98,
    tags: ['fastback', 'Highland Green', 'Bullitt', 'show quality'],
    capturedAt: '2024-10-01T14:30:00Z',
    classifiedAt: '2024-10-01T14:31:00Z',
    isHeroImage: true,
  },
  {
    id: 'img-002-004',
    vehicleId: 'v-002',
    filename: 'mustang_interior_seats.jpg',
    url: placeholderUrl('mustang-int-1', 'full'),
    thumbnailUrl: placeholderUrl('mustang-int-1', 'thumb'),
    mediumUrl: placeholderUrl('mustang-int-1', 'medium'),
    component: 'interior',
    componentDetail: 'bucket seats',
    condition: 'restored',
    phase: 'assembly',
    confidence: 91,
    tags: ['bucket seats', 'black vinyl', 'pony interior', 'restored'],
    capturedAt: '2024-09-20T11:00:00Z',
    classifiedAt: '2024-09-20T11:01:00Z',
    isHeroImage: false,
  },
  {
    id: 'img-002-005',
    vehicleId: 'v-002',
    filename: 'mustang_suspension_front.jpg',
    url: placeholderUrl('mustang-susp-1', 'full'),
    thumbnailUrl: placeholderUrl('mustang-susp-1', 'thumb'),
    mediumUrl: placeholderUrl('mustang-susp-1', 'medium'),
    component: 'suspension',
    componentDetail: 'front suspension',
    condition: 'installed',
    phase: 'assembly',
    confidence: 89,
    tags: ['front suspension', 'disc brakes', 'export brace', 'Monte Carlo bar'],
    capturedAt: '2024-07-10T09:30:00Z',
    classifiedAt: '2024-07-10T09:31:00Z',
    isHeroImage: false,
  },
  {
    id: 'img-002-006',
    vehicleId: 'v-002',
    filename: 'mustang_drivetrain_toploader.jpg',
    url: placeholderUrl('mustang-trans-1', 'full'),
    thumbnailUrl: placeholderUrl('mustang-trans-1', 'thumb'),
    mediumUrl: placeholderUrl('mustang-trans-1', 'medium'),
    component: 'drivetrain',
    componentDetail: 'Toploader 4-speed',
    condition: 'restored',
    phase: 'restoration',
    confidence: 94,
    tags: ['Toploader', '4-speed', 'close ratio', 'rebuilt'],
    capturedAt: '2024-06-05T13:00:00Z',
    classifiedAt: '2024-06-05T13:01:00Z',
    isHeroImage: false,
  },
  {
    id: 'img-002-007',
    vehicleId: 'v-002',
    filename: 'mustang_brakes_front.jpg',
    url: placeholderUrl('mustang-brake-1', 'full'),
    thumbnailUrl: placeholderUrl('mustang-brake-1', 'thumb'),
    mediumUrl: placeholderUrl('mustang-brake-1', 'medium'),
    component: 'brakes',
    componentDetail: 'front disc brakes',
    condition: 'installed',
    phase: 'assembly',
    confidence: 96,
    tags: ['disc brakes', 'Kelsey Hayes', 'power assist', 'new rotors'],
    capturedAt: '2024-07-12T10:00:00Z',
    classifiedAt: '2024-07-12T10:01:00Z',
    isHeroImage: false,
  },
  {
    id: 'img-002-008',
    vehicleId: 'v-002',
    filename: 'mustang_steering_wheel.jpg',
    url: placeholderUrl('mustang-steer-1', 'full'),
    thumbnailUrl: placeholderUrl('mustang-steer-1', 'thumb'),
    mediumUrl: placeholderUrl('mustang-steer-1', 'medium'),
    component: 'steering',
    componentDetail: 'wood rim steering wheel',
    condition: 'restored',
    phase: 'finishing',
    confidence: 93,
    tags: ['wood wheel', 'Shelby style', 'restored', 'correct'],
    capturedAt: '2024-11-05T14:00:00Z',
    classifiedAt: '2024-11-05T14:01:00Z',
    isHeroImage: false,
  },
];

/**
 * Demo images for the 1973 Porsche 911 (v-003)
 */
const porscheImages: ImageMetadata[] = [
  {
    id: 'img-003-001',
    vehicleId: 'v-003',
    filename: 'porsche_engine_flat6.jpg',
    url: placeholderUrl('porsche-engine-1', 'full'),
    thumbnailUrl: placeholderUrl('porsche-engine-1', 'thumb'),
    mediumUrl: placeholderUrl('porsche-engine-1', 'medium'),
    component: 'engine',
    componentDetail: '2.7L flat-6 RS',
    condition: 'restored',
    phase: 'assembly',
    confidence: 98,
    tags: ['flat-6', '2.7L', 'RS', 'MFI', 'numbers matching'],
    capturedAt: '2024-09-01T11:00:00Z',
    classifiedAt: '2024-09-01T11:01:00Z',
    isHeroImage: true,
  },
  {
    id: 'img-003-002',
    vehicleId: 'v-003',
    filename: 'porsche_body_rs_ducktail.jpg',
    url: placeholderUrl('porsche-body-1', 'full'),
    thumbnailUrl: placeholderUrl('porsche-body-1', 'thumb'),
    mediumUrl: placeholderUrl('porsche-body-1', 'medium'),
    component: 'body',
    componentDetail: 'RS ducktail spoiler',
    condition: 'painted',
    phase: 'finishing',
    confidence: 97,
    tags: ['ducktail', 'RS', 'Guards Red', 'lightweight'],
    capturedAt: '2024-10-15T15:00:00Z',
    classifiedAt: '2024-10-15T15:01:00Z',
    isHeroImage: true,
  },
  {
    id: 'img-003-003',
    vehicleId: 'v-003',
    filename: 'porsche_interior_rs.jpg',
    url: placeholderUrl('porsche-int-1', 'full'),
    thumbnailUrl: placeholderUrl('porsche-int-1', 'thumb'),
    mediumUrl: placeholderUrl('porsche-int-1', 'medium'),
    component: 'interior',
    componentDetail: 'lightweight RS interior',
    condition: 'restored',
    phase: 'finishing',
    confidence: 95,
    tags: ['RS interior', 'lightweight', 'sport seats', 'houndstooth'],
    capturedAt: '2024-11-01T10:00:00Z',
    classifiedAt: '2024-11-01T10:01:00Z',
    isHeroImage: false,
  },
  {
    id: 'img-003-004',
    vehicleId: 'v-003',
    filename: 'porsche_suspension_rear.jpg',
    url: placeholderUrl('porsche-susp-1', 'full'),
    thumbnailUrl: placeholderUrl('porsche-susp-1', 'thumb'),
    mediumUrl: placeholderUrl('porsche-susp-1', 'medium'),
    component: 'suspension',
    componentDetail: 'rear trailing arms',
    condition: 'installed',
    phase: 'assembly',
    confidence: 91,
    tags: ['trailing arms', 'Bilstein', 'lowered', 'RS spec'],
    capturedAt: '2024-08-20T14:00:00Z',
    classifiedAt: '2024-08-20T14:01:00Z',
    isHeroImage: false,
  },
  {
    id: 'img-003-005',
    vehicleId: 'v-003',
    filename: 'porsche_wheels_fuchs.jpg',
    url: placeholderUrl('porsche-wheel-1', 'full'),
    thumbnailUrl: placeholderUrl('porsche-wheel-1', 'thumb'),
    mediumUrl: placeholderUrl('porsche-wheel-1', 'medium'),
    component: 'wheels',
    componentDetail: 'Fuchs alloy wheels',
    condition: 'restored',
    phase: 'finishing',
    confidence: 99,
    tags: ['Fuchs', '15x8', '15x9', 'black center', 'polished lip'],
    capturedAt: '2024-10-20T09:00:00Z',
    classifiedAt: '2024-10-20T09:01:00Z',
    isHeroImage: false,
  },
  {
    id: 'img-003-006',
    vehicleId: 'v-003',
    filename: 'porsche_brakes_front.jpg',
    url: placeholderUrl('porsche-brake-1', 'full'),
    thumbnailUrl: placeholderUrl('porsche-brake-1', 'thumb'),
    mediumUrl: placeholderUrl('porsche-brake-1', 'medium'),
    component: 'brakes',
    componentDetail: 'front ventilated discs',
    condition: 'installed',
    phase: 'assembly',
    confidence: 94,
    tags: ['ventilated', 'S calipers', 'cross-drilled', 'upgraded'],
    capturedAt: '2024-08-25T11:30:00Z',
    classifiedAt: '2024-08-25T11:31:00Z',
    isHeroImage: false,
  },
];

/**
 * Demo images for the 1963 Corvette Sting Ray (v-004)
 */
const corvetteImages: ImageMetadata[] = [
  {
    id: 'img-004-001',
    vehicleId: 'v-004',
    filename: 'corvette_body_splitwindow.jpg',
    url: placeholderUrl('corvette-body-1', 'full'),
    thumbnailUrl: placeholderUrl('corvette-body-1', 'thumb'),
    mediumUrl: placeholderUrl('corvette-body-1', 'medium'),
    component: 'body',
    componentDetail: 'split window coupe',
    condition: 'painted',
    phase: 'finishing',
    confidence: 99,
    tags: ['split window', 'coupe', 'Riverside Blue', '1963 only'],
    capturedAt: '2024-09-10T14:00:00Z',
    classifiedAt: '2024-09-10T14:01:00Z',
    isHeroImage: true,
  },
  {
    id: 'img-004-002',
    vehicleId: 'v-004',
    filename: 'corvette_engine_327_fuelie.jpg',
    url: placeholderUrl('corvette-engine-1', 'full'),
    thumbnailUrl: placeholderUrl('corvette-engine-1', 'thumb'),
    mediumUrl: placeholderUrl('corvette-engine-1', 'medium'),
    component: 'engine',
    componentDetail: '327 fuel injection',
    condition: 'restored',
    phase: 'assembly',
    confidence: 96,
    tags: ['327', 'fuelie', 'Rochester', '360hp', 'numbers matching'],
    capturedAt: '2024-08-01T10:00:00Z',
    classifiedAt: '2024-08-01T10:01:00Z',
    isHeroImage: true,
  },
  {
    id: 'img-004-003',
    vehicleId: 'v-004',
    filename: 'corvette_interior_dash.jpg',
    url: placeholderUrl('corvette-int-1', 'full'),
    thumbnailUrl: placeholderUrl('corvette-int-1', 'thumb'),
    mediumUrl: placeholderUrl('corvette-int-1', 'medium'),
    component: 'interior',
    componentDetail: 'dashboard and console',
    condition: 'restored',
    phase: 'finishing',
    confidence: 93,
    tags: ['dashboard', 'saddle leather', 'tachometer', 'correct'],
    capturedAt: '2024-10-01T11:00:00Z',
    classifiedAt: '2024-10-01T11:01:00Z',
    isHeroImage: false,
  },
  {
    id: 'img-004-004',
    vehicleId: 'v-004',
    filename: 'corvette_suspension_irs.jpg',
    url: placeholderUrl('corvette-susp-1', 'full'),
    thumbnailUrl: placeholderUrl('corvette-susp-1', 'thumb'),
    mediumUrl: placeholderUrl('corvette-susp-1', 'medium'),
    component: 'suspension',
    componentDetail: 'independent rear suspension',
    condition: 'installed',
    phase: 'assembly',
    confidence: 90,
    tags: ['IRS', 'transverse spring', 'half shafts', 'rebuilt'],
    capturedAt: '2024-07-15T13:00:00Z',
    classifiedAt: '2024-07-15T13:01:00Z',
    isHeroImage: false,
  },
  {
    id: 'img-004-005',
    vehicleId: 'v-004',
    filename: 'corvette_glass_rear.jpg',
    url: placeholderUrl('corvette-glass-1', 'full'),
    thumbnailUrl: placeholderUrl('corvette-glass-1', 'thumb'),
    mediumUrl: placeholderUrl('corvette-glass-1', 'medium'),
    component: 'glass',
    componentDetail: 'split rear window',
    condition: 'installed',
    phase: 'finishing',
    confidence: 97,
    tags: ['split window', 'tinted', 'original', 'rare'],
    capturedAt: '2024-09-15T15:30:00Z',
    classifiedAt: '2024-09-15T15:31:00Z',
    isHeroImage: false,
  },
];

/**
 * Demo images for the 1955 Mercedes 300SL Gullwing (v-005)
 */
const mercedesImages: ImageMetadata[] = [
  {
    id: 'img-005-001',
    vehicleId: 'v-005',
    filename: 'gullwing_body_doors_open.jpg',
    url: placeholderUrl('mercedes-body-1', 'full'),
    thumbnailUrl: placeholderUrl('mercedes-body-1', 'thumb'),
    mediumUrl: placeholderUrl('mercedes-body-1', 'medium'),
    component: 'body',
    componentDetail: 'gullwing doors open',
    condition: 'restored',
    phase: 'completed',
    confidence: 99,
    tags: ['gullwing', 'doors', 'Silver', 'iconic', 'museum quality'],
    capturedAt: '2024-11-15T14:00:00Z',
    classifiedAt: '2024-11-15T14:01:00Z',
    isHeroImage: true,
  },
  {
    id: 'img-005-002',
    vehicleId: 'v-005',
    filename: 'gullwing_engine_m198.jpg',
    url: placeholderUrl('mercedes-engine-1', 'full'),
    thumbnailUrl: placeholderUrl('mercedes-engine-1', 'thumb'),
    mediumUrl: placeholderUrl('mercedes-engine-1', 'medium'),
    component: 'engine',
    componentDetail: 'M198 3.0L inline-6',
    condition: 'restored',
    phase: 'completed',
    confidence: 98,
    tags: ['M198', 'fuel injection', 'inline-6', 'Bosch', 'canted'],
    capturedAt: '2024-11-10T10:00:00Z',
    classifiedAt: '2024-11-10T10:01:00Z',
    isHeroImage: true,
  },
  {
    id: 'img-005-003',
    vehicleId: 'v-005',
    filename: 'gullwing_interior_cockpit.jpg',
    url: placeholderUrl('mercedes-int-1', 'full'),
    thumbnailUrl: placeholderUrl('mercedes-int-1', 'thumb'),
    mediumUrl: placeholderUrl('mercedes-int-1', 'medium'),
    component: 'interior',
    componentDetail: 'cockpit and gauges',
    condition: 'restored',
    phase: 'completed',
    confidence: 96,
    tags: ['cockpit', 'red leather', 'VDO gauges', 'correct'],
    capturedAt: '2024-11-12T11:30:00Z',
    classifiedAt: '2024-11-12T11:31:00Z',
    isHeroImage: false,
  },
  {
    id: 'img-005-004',
    vehicleId: 'v-005',
    filename: 'gullwing_suspension_front.jpg',
    url: placeholderUrl('mercedes-susp-1', 'full'),
    thumbnailUrl: placeholderUrl('mercedes-susp-1', 'thumb'),
    mediumUrl: placeholderUrl('mercedes-susp-1', 'medium'),
    component: 'suspension',
    componentDetail: 'front double wishbone',
    condition: 'installed',
    phase: 'assembly',
    confidence: 92,
    tags: ['double wishbone', 'coil springs', 'rebuilt', 'correct'],
    capturedAt: '2024-10-05T09:00:00Z',
    classifiedAt: '2024-10-05T09:01:00Z',
    isHeroImage: false,
  },
  {
    id: 'img-005-005',
    vehicleId: 'v-005',
    filename: 'gullwing_wheels_rudge.jpg',
    url: placeholderUrl('mercedes-wheel-1', 'full'),
    thumbnailUrl: placeholderUrl('mercedes-wheel-1', 'thumb'),
    mediumUrl: placeholderUrl('mercedes-wheel-1', 'medium'),
    component: 'wheels',
    componentDetail: 'Rudge knock-off wheels',
    condition: 'restored',
    phase: 'finishing',
    confidence: 95,
    tags: ['Rudge', 'knock-off', 'chrome', 'correct', 'period'],
    capturedAt: '2024-10-25T14:30:00Z',
    classifiedAt: '2024-10-25T14:31:00Z',
    isHeroImage: false,
  },
  {
    id: 'img-005-006',
    vehicleId: 'v-005',
    filename: 'gullwing_frame_tubular.jpg',
    url: placeholderUrl('mercedes-frame-1', 'full'),
    thumbnailUrl: placeholderUrl('mercedes-frame-1', 'thumb'),
    mediumUrl: placeholderUrl('mercedes-frame-1', 'medium'),
    component: 'body',
    componentDetail: 'tubular space frame',
    condition: 'stripped',
    phase: 'teardown',
    confidence: 94,
    tags: ['space frame', 'tubular', 'bare', 'inspection'],
    capturedAt: '2024-02-15T10:00:00Z',
    classifiedAt: '2024-02-15T10:01:00Z',
    isHeroImage: false,
  },
];

/**
 * Demo images for the 1969 Pontiac GTO Judge (v-006)
 */
const gtoImages: ImageMetadata[] = [
  {
    id: 'img-006-001',
    vehicleId: 'v-006',
    filename: 'gto_engine_ram_air.jpg',
    url: placeholderUrl('gto-engine-1', 'full'),
    thumbnailUrl: placeholderUrl('gto-engine-1', 'thumb'),
    mediumUrl: placeholderUrl('gto-engine-1', 'medium'),
    component: 'engine',
    componentDetail: '400 Ram Air III',
    condition: 'restored',
    phase: 'assembly',
    confidence: 97,
    tags: ['400', 'Ram Air III', 'Judge', 'numbers matching', 'detailed'],
    capturedAt: '2024-10-01T11:00:00Z',
    classifiedAt: '2024-10-01T11:01:00Z',
    isHeroImage: true,
  },
  {
    id: 'img-006-002',
    vehicleId: 'v-006',
    filename: 'gto_body_judge_stripe.jpg',
    url: placeholderUrl('gto-body-1', 'full'),
    thumbnailUrl: placeholderUrl('gto-body-1', 'thumb'),
    mediumUrl: placeholderUrl('gto-body-1', 'medium'),
    component: 'body',
    componentDetail: 'Judge graphics package',
    condition: 'painted',
    phase: 'finishing',
    confidence: 96,
    tags: ['Judge', 'stripes', 'Starlight Black', 'decals', 'correct'],
    capturedAt: '2024-11-01T15:00:00Z',
    classifiedAt: '2024-11-01T15:01:00Z',
    isHeroImage: true,
  },
  {
    id: 'img-006-003',
    vehicleId: 'v-006',
    filename: 'gto_interior_bucket.jpg',
    url: placeholderUrl('gto-int-1', 'full'),
    thumbnailUrl: placeholderUrl('gto-int-1', 'thumb'),
    mediumUrl: placeholderUrl('gto-int-1', 'medium'),
    component: 'interior',
    componentDetail: 'bucket seats and console',
    condition: 'restored',
    phase: 'finishing',
    confidence: 93,
    tags: ['bucket seats', 'console', 'Hurst shifter', 'black vinyl'],
    capturedAt: '2024-11-10T10:30:00Z',
    classifiedAt: '2024-11-10T10:31:00Z',
    isHeroImage: false,
  },
  {
    id: 'img-006-004',
    vehicleId: 'v-006',
    filename: 'gto_hood_scoops.jpg',
    url: placeholderUrl('gto-hood-1', 'full'),
    thumbnailUrl: placeholderUrl('gto-hood-1', 'thumb'),
    mediumUrl: placeholderUrl('gto-hood-1', 'medium'),
    component: 'body',
    componentDetail: 'Ram Air hood scoops',
    condition: 'installed',
    phase: 'finishing',
    confidence: 95,
    tags: ['Ram Air', 'hood scoops', 'functional', 'Judge'],
    capturedAt: '2024-11-05T14:00:00Z',
    classifiedAt: '2024-11-05T14:01:00Z',
    isHeroImage: false,
  },
  {
    id: 'img-006-005',
    vehicleId: 'v-006',
    filename: 'gto_exhaust_tips.jpg',
    url: placeholderUrl('gto-exh-1', 'full'),
    thumbnailUrl: placeholderUrl('gto-exh-1', 'thumb'),
    mediumUrl: placeholderUrl('gto-exh-1', 'medium'),
    component: 'exhaust',
    componentDetail: 'dual exhaust tips',
    condition: 'installed',
    phase: 'finishing',
    confidence: 91,
    tags: ['dual exhaust', 'chrome tips', 'splitters', 'correct'],
    capturedAt: '2024-11-08T13:00:00Z',
    classifiedAt: '2024-11-08T13:01:00Z',
    isHeroImage: false,
  },
];

/**
 * Generate additional test images to simulate a large dataset
 * Creates 500+ images across all vehicles and components
 */
function generateAdditionalImages(): ImageMetadata[] {
  const components: ComponentCategory[] = ['engine', 'suspension', 'interior', 'body', 'electrical', 'drivetrain', 'brakes', 'steering'];
  const conditions: ConditionState[] = ['as-received', 'disassembly', 'restored', 'installed'];
  const phases: RestorePhase[] = ['initial-assessment', 'teardown', 'restoration', 'assembly', 'finishing'];
  const vehicleIds = ['v-001', 'v-002', 'v-003', 'v-004', 'v-005', 'v-006'];

  const componentDetails: Record<ComponentCategory, string[]> = {
    engine: ['valve covers', 'intake manifold', 'carburetor rebuild', 'timing chain', 'oil pan', 'heads', 'block machining', 'cam install'],
    suspension: ['control arms', 'sway bar', 'shocks', 'springs', 'bushings', 'tie rods', 'ball joints', 'steering box'],
    interior: ['door panels', 'carpet install', 'headliner', 'dash restoration', 'gauge cluster', 'steering wheel', 'seats', 'console'],
    body: ['fender repair', 'quarter panel', 'door alignment', 'hood fit', 'trunk lid', 'rocker panels', 'floor pans', 'firewall'],
    electrical: ['wiring harness', 'alternator', 'starter motor', 'gauges', 'fuse box', 'headlights', 'tail lights', 'horn'],
    drivetrain: ['transmission', 'clutch', 'driveshaft', 'differential', 'axles', 'U-joints', 'flywheel', 'bell housing'],
    brakes: ['master cylinder', 'wheel cylinders', 'brake lines', 'rotors', 'drums', 'calipers', 'pads', 'shoes'],
    steering: ['steering column', 'steering gear', 'pitman arm', 'idler arm', 'drag link', 'power steering pump', 'hoses', 'reservoir'],
    fuel: ['fuel tank', 'fuel lines', 'fuel pump', 'sending unit'],
    exhaust: ['headers', 'mufflers', 'pipes', 'tips'],
    wheels: ['wheel restoration', 'tire mounting', 'balancing', 'center caps'],
    cooling: ['radiator', 'water pump', 'thermostat', 'hoses'],
    trim: ['chrome trim', 'moldings', 'emblems', 'badges'],
    glass: ['windshield', 'side glass', 'rear glass', 'seals'],
    other: ['misc parts', 'hardware', 'fasteners', 'clips'],
  };

  const tagSets: Record<ComponentCategory, string[][]> = {
    engine: [['rebuild', 'machined'], ['detailed', 'painted'], ['original', 'numbers matching'], ['restored', 'rebuilt']],
    suspension: [['polyurethane', 'bushings'], ['upgraded', 'lowered'], ['stock', 'correct'], ['rebuilt', 'aligned']],
    interior: [['original', 'correct'], ['restored', 'dye-matched'], ['new', 'reproduction'], ['period', 'correct']],
    body: [['media blasted', 'primed'], ['filled', 'blocked'], ['painted', 'cleared'], ['assembled', 'fit']],
    electrical: [['rewired', 'upgraded'], ['restored', 'working'], ['original', 'correct'], ['tested', 'functional']],
    drivetrain: [['rebuilt', 'sealed'], ['upgraded', 'strengthened'], ['restored', 'balanced'], ['installed', 'aligned']],
    brakes: [['rebuilt', 'sleeved'], ['upgraded', 'disc'], ['restored', 'working'], ['bled', 'tested']],
    steering: [['rebuilt', 'tight'], ['upgraded', 'quick ratio'], ['restored', 'correct'], ['aligned', 'centered']],
    fuel: [['cleaned', 'sealed'], ['new', 'lines'], ['restored', 'working']],
    exhaust: [['new', 'stainless'], ['restored', 'coated'], ['installed', 'fitted']],
    wheels: [['restored', 'polished'], ['new', 'tires'], ['balanced', 'mounted']],
    cooling: [['new', 'aluminum'], ['restored', 'rodded'], ['tested', 'pressured']],
    trim: [['rechrome', 'polished'], ['restored', 'correct'], ['new', 'reproduction']],
    glass: [['new', 'sealed'], ['restored', 'clear'], ['tinted', 'installed']],
    other: [['sorted', 'labeled'], ['restored', 'plated'], ['new', 'correct']],
  };

  const generatedImages: ImageMetadata[] = [];
  let idCounter = 100;

  // Generate 500 additional images
  for (let i = 0; i < 500; i++) {
    const vehicleId = vehicleIds[i % vehicleIds.length];
    const component = components[Math.floor(i / 8) % components.length];
    const condition = conditions[i % conditions.length];
    const phase = phases[Math.floor(i / 4) % phases.length];
    const details = componentDetails[component] || componentDetails['engine'];
    const detail = details[i % details.length];
    const tags = tagSets[component]?.[i % (tagSets[component]?.length || 1)] || ['restored'];

    // Create consistent but varied dates
    const baseDate = new Date('2024-01-01');
    baseDate.setDate(baseDate.getDate() + (i % 365));
    baseDate.setHours(8 + (i % 10), (i * 7) % 60);

    generatedImages.push({
      id: `img-gen-${String(idCounter++).padStart(4, '0')}`,
      vehicleId,
      filename: `${component}_${detail.replace(/\s+/g, '_')}_${idCounter}.jpg`,
      url: placeholderUrl(`gen-${idCounter}-${component}`, 'full'),
      thumbnailUrl: placeholderUrl(`gen-${idCounter}-${component}`, 'thumb'),
      mediumUrl: placeholderUrl(`gen-${idCounter}-${component}`, 'medium'),
      component,
      componentDetail: detail,
      condition,
      phase,
      confidence: 85 + (i % 15), // 85-99%
      tags: [...tags, component, condition],
      capturedAt: baseDate.toISOString(),
      classifiedAt: new Date(baseDate.getTime() + 60000).toISOString(),
      isHeroImage: i % 50 === 0, // Every 50th is a hero
    });
  }

  return generatedImages;
}

/**
 * All demo images combined (base + generated for testing)
 */
const baseImages: ImageMetadata[] = [
  ...chevelleImages,
  ...mustangImages,
  ...porscheImages,
  ...corvetteImages,
  ...mercedesImages,
  ...gtoImages,
];

// Add generated images for stress testing (540+ total)
export const demoImages: ImageMetadata[] = [
  ...baseImages,
  ...generateAdditionalImages(),
];

/**
 * Get images by vehicle ID
 */
export function getImagesByVehicle(vehicleId: string): ImageMetadata[] {
  return demoImages.filter(img => img.vehicleId === vehicleId);
}

/**
 * Get hero images for showcase mode
 */
export function getHeroImages(): ImageMetadata[] {
  return demoImages.filter(img => img.isHeroImage);
}

/**
 * Get images by component
 */
export function getImagesByComponent(component: ComponentCategory): ImageMetadata[] {
  return demoImages.filter(img => img.component === component);
}

/**
 * Get images by condition
 */
export function getImagesByCondition(condition: ConditionState): ImageMetadata[] {
  return demoImages.filter(img => img.condition === condition);
}

/**
 * Get comparison pairs (before/after)
 */
export function getComparisonPairs(): { before: ImageMetadata; after: ImageMetadata }[] {
  const pairs: { before: ImageMetadata; after: ImageMetadata }[] = [];

  demoImages.forEach(img => {
    if (img.afterImageId) {
      const afterImg = demoImages.find(i => i.id === img.afterImageId);
      if (afterImg) {
        pairs.push({ before: img, after: afterImg });
      }
    }
  });

  return pairs;
}

/**
 * Get image by ID
 */
export function getImageById(id: string): ImageMetadata | undefined {
  return demoImages.find(img => img.id === id);
}
